import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardTitle, Field, Input, useToast } from "@/core/ui";
import { downloadJson, useDeleteAccount, useExportMyData } from "./api";

/**
 * GDPR controls: take your data with you, or delete the account. Deleting removes the
 * attempts and anonymises the leaderboard entries; it never rewrites other people's ranks.
 */
export function MyDataCard({ email }: { email: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const exporter = useExportMyData();
  const remover = useDeleteAccount();
  const [confirmation, setConfirmation] = useState("");
  const matches = confirmation.trim().toLowerCase() === email.toLowerCase();

  const exportData = () =>
    exporter.mutate(undefined, {
      onSuccess: (data) => {
        try {
          downloadJson("my-brainteaser-data.json", data);
        } catch {
          toast(t("app.error_generic"), "danger");
        }
      },
      onError: () => toast(t("app.error_generic"), "danger"),
    });

  const deleteAccount = () =>
    remover.mutate(undefined, {
      onSuccess: () => window.location.assign("/auth/login"),
      onError: () => toast(t("app.error_generic"), "danger"),
    });

  return (
    <Card className="bt-account__data">
      <CardTitle>{t("account.data.title")}</CardTitle>
      <p className="bt-account__lead">{t("account.data.export_hint")}</p>
      <Button variant="outline" onClick={exportData} loading={exporter.isPending}>
        {t("account.data.export")}
      </Button>

      <hr className="bt-account__divider" />

      <h3 className="bt-account__danger-title">{t("account.data.delete")}</h3>
      <Alert tone="danger">{t("account.data.delete_hint")}</Alert>
      <Field
        label={t("account.data.confirm_label", { email })}
        htmlFor="delete-confirm"
        hint={t("account.data.confirm_hint")}
      >
        <Input
          id="delete-confirm"
          value={confirmation}
          autoComplete="off"
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </Field>
      <Button
        variant="danger"
        disabled={!matches}
        loading={remover.isPending}
        onClick={deleteAccount}
      >
        {t("account.data.delete_action")}
      </Button>
    </Card>
  );
}
